import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BetriebskontakteService } from './betriebskontakte.service';
import { Betriebskontakt } from './betriebskontakt';
import {
    BetriebskontakteNeuerKontaktDialogComponent,
    NeuerKontaktDialogResult,
} from './betriebskontakte-neuer-kontakt-dialog.component';

export interface BetriebskontaktAuswahlConfig {
    showLandwirte?: boolean;
    showHaendler?: boolean;
    showSchlachthoefe?: boolean;
    showAll?: boolean;
    multiSelect?: boolean;
    compact?: boolean;
    title?: string;
    placeholder?: string;
    betriebId?: number;
    adminMode?: boolean;
    zusaetzlicheKontakte?: any[];
    allowCreateNew?: boolean;
    keepOpenOnSelect?: boolean; // Neue Option: Dialog offen halten nach Auswahl
    /** Modern picker (e.g. Empfänger dialog): list visible immediately, no search-focus expand. */
    alwaysExpanded?: boolean;
    showApplyButton?: boolean;
    modernStyle?: boolean;
}

@Component({
    selector: 'kiene-betriebskontakt-auswahl',
    templateUrl: './betriebskontakt-auswahl.component.html',
    styleUrls: ['./betriebskontakt-auswahl.component.scss'],
})
export class BetriebskontaktAuswahlComponent implements OnInit, OnDestroy, OnChanges {
    @Input() config: BetriebskontaktAuswahlConfig = {
        showLandwirte: true,
        showHaendler: true,
        showSchlachthoefe: true,
        showAll: true,
        multiSelect: false,
        compact: false,
        title: 'Betriebskontakt auswählen',
        placeholder: 'Kontakt suchen...',
        allowCreateNew: true,
        keepOpenOnSelect: false, // Standard: Dialog schließt sich nach Auswahl
        showApplyButton: true,
        modernStyle: false,
    };

    @Input() selectedKontakte: Betriebskontakt[] = [];
    @Input() disabled: boolean = false;
    @Input() kontakteOverride?: Betriebskontakt[] | null;

    @Output() kontaktSelected = new EventEmitter<Betriebskontakt>();
    @Output() kontakteSelected = new EventEmitter<Betriebskontakt[]>();
    @Output() selectionChanged = new EventEmitter<Betriebskontakt[]>();

    kontakte: Betriebskontakt[] = [];
    filteredKontakte: Betriebskontakt[] = [];
    selectedFilter = 'all';
    ladeKontakte = false;
    searchTerm = '';
    isExpanded = false; // Steuert, ob der erweiterte Bereich sichtbar ist
    private preventBlur = false; // Verhindert das Schließen bei Multi-Select
    private kontakteListGeneration = 0;

    private destroy$ = new Subject<void>();

    constructor(
        private betriebskontakteService: BetriebskontakteService,
        private dialog: MatDialog,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['kontakteOverride'] && changes['kontakteOverride'].currentValue !== undefined) {
            this.applyKontakteOverride(changes['kontakteOverride'].currentValue);
        }
        if (changes['selectedKontakte']) {
            this.selectedKontakte = [...(changes['selectedKontakte'].currentValue || [])];
        }
        if (changes['config'] && !changes['config'].firstChange) {
            this.mergeZusaetzlicheKontakte();
            this.deduplicateKontakte();
            this.updateFilter();
            this.applyExpandedFromConfig();
        }
    }

    ngOnInit(): void {
        this.selectedKontakte = [...(this.selectedKontakte || [])];
        this.applyExpandedFromConfig();
        this.ladeKontakteList();
        this.updateFilter();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private applyExpandedFromConfig(): void {
        if (this.config.alwaysExpanded || (this.config.modernStyle && this.config.keepOpenOnSelect)) {
            this.isExpanded = true;
        }
    }

    private ladeKontakteList(afterLoad?: () => void): void {
        if (this.kontakteOverride !== undefined) {
            this.applyKontakteOverride(this.kontakteOverride);
            afterLoad?.();
            return;
        }

        const generation = ++this.kontakteListGeneration;
        this.ladeKontakte = true;

        const onLoaded = (records: Betriebskontakt[]) => {
            if (generation !== this.kontakteListGeneration) {
                return;
            }
            this.kontakte = records ?? [];
            this.mergeZusaetzlicheKontakte();
            this.mergeSelectedKontakteIntoList();
            this.deduplicateKontakte();
            this.normalizeSelectedKontakteToList();
            this.updateFilter();
            this.ladeKontakte = false;
            this.applyExpandedFromConfig();
            afterLoad?.();
        };

        // Admin-Modus: Lade alle Kontakte für den spezifischen Betrieb
        if (this.config.adminMode && this.config.betriebId) {
            this.betriebskontakteService
                .getAlleKontakteFuerBetrieb(this.config.betriebId)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: response => onLoaded(response.records),
                    error: () => {
                        this.ladeKontakte = false;
                    },
                });
        } else {
            // Normaler Modus: Lade eigene Kontakte
            this.betriebskontakteService
                .getBetriebskontakte()
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: response => onLoaded(response.records),
                    error: () => {
                        this.ladeKontakte = false;
                    },
                });
        }
    }

    private applyKontakteOverride(override: Betriebskontakt[] | null) {
        this.ladeKontakte = false;
        if (override === null) {
            this.kontakte = [];
            this.updateFilter();
            return;
        }
        this.kontakte = override ? [...override] : [];
        this.mergeZusaetzlicheKontakte();
        this.deduplicateKontakte();
        this.updateFilter();
    }

    /** Pre-selected tierliste contacts may not be in the user's loaded list yet. */
    private mergeZusaetzlicheKontakte(): void {
        const extra = this.config.zusaetzlicheKontakte;
        if (!extra?.length) {
            return;
        }
        for (const kontakt of extra) {
            if (!this.findKontaktInList(kontakt)) {
                this.kontakte.push(kontakt);
            }
        }
    }

    private normalizeKontaktId(value: unknown): number {
        return Number(value) || 0;
    }

    private normalizeKundeId(value: unknown): number {
        return Number(value) || 0;
    }

    private isSameKontakt(a: Betriebskontakt, b: Betriebskontakt): boolean {
        const aKontaktId = this.normalizeKontaktId(a?.kontakt_id);
        const bKontaktId = this.normalizeKontaktId(b?.kontakt_id);
        if (aKontaktId > 0 && bKontaktId > 0 && aKontaktId === bKontaktId) {
            return true;
        }
        const aKundeId = this.normalizeKundeId(a?.kunde_id);
        const bKundeId = this.normalizeKundeId(b?.kunde_id);
        if (aKundeId > 0 && aKundeId === bKundeId) {
            return true;
        }
        const aVvvo = String(a?.vvvo ?? '').trim();
        const bVvvo = String(b?.vvvo ?? '').trim();
        return aVvvo.length === 15 && aVvvo === bVvvo;
    }

    private findKontaktInList(kontakt: Betriebskontakt): Betriebskontakt | undefined {
        return this.kontakte.find(k => this.isSameKontakt(k, kontakt));
    }

    /** One row per kunde_id / kontakt_id / VVVO; prefer API rows with a real kontakt_id. */
    private deduplicateKontakte(): void {
        const byKundeId = new Map<number, Betriebskontakt>();
        const byVvvo = new Map<string, Betriebskontakt>();
        const withoutKey: Betriebskontakt[] = [];

        const keepBetter = (existing: Betriebskontakt, next: Betriebskontakt): Betriebskontakt => {
            const existingHasId = this.normalizeKontaktId(existing.kontakt_id) > 0;
            const nextHasId = this.normalizeKontaktId(next.kontakt_id) > 0;
            if (!existingHasId && nextHasId) {
                return next;
            }
            return existing;
        };

        for (const kontakt of this.kontakte) {
            const kundeId = this.normalizeKundeId(kontakt?.kunde_id);
            const vvvo = String(kontakt?.vvvo ?? '').trim();

            if (kundeId > 0) {
                const existing = byKundeId.get(kundeId);
                byKundeId.set(kundeId, existing ? keepBetter(existing, kontakt) : kontakt);
                if (vvvo.length === 15) {
                    const vvvoExisting = byVvvo.get(vvvo);
                    byVvvo.set(vvvo, vvvoExisting ? keepBetter(vvvoExisting, kontakt) : kontakt);
                }
                continue;
            }

            if (vvvo.length === 15) {
                const existing = byVvvo.get(vvvo);
                byVvvo.set(vvvo, existing ? keepBetter(existing, kontakt) : kontakt);
                continue;
            }

            withoutKey.push(kontakt);
        }

        const merged = new Map<string, Betriebskontakt>();
        for (const kontakt of [...byKundeId.values(), ...byVvvo.values(), ...withoutKey]) {
            const key = this.kontaktIdentityKey(kontakt);
            const existing = merged.get(key);
            merged.set(key, existing ? keepBetter(existing, kontakt) : kontakt);
        }
        this.kontakte = [...merged.values()];
    }

    private kontaktIdentityKey(kontakt: Betriebskontakt): string {
        const kontaktId = this.normalizeKontaktId(kontakt?.kontakt_id);
        if (kontaktId > 0) {
            return `kontakt:${kontaktId}`;
        }
        const kundeId = this.normalizeKundeId(kontakt?.kunde_id);
        if (kundeId > 0) {
            return `kunde:${kundeId}`;
        }
        const vvvo = String(kontakt?.vvvo ?? '').trim();
        if (vvvo.length === 15) {
            return `vvvo:${vvvo}`;
        }
        return `row:${kontakt?.bezeichnung_final ?? ''}:${kontakt?.zusatz_final ?? ''}`;
    }

    /** Replace selection stubs (e.g. after VVVO add) with the canonical list row. */
    private normalizeSelectedKontakteToList(): void {
        if (!this.selectedKontakte?.length) {
            return;
        }
        const normalized: Betriebskontakt[] = [];
        for (const selected of this.selectedKontakte) {
            const canonical = this.findKontaktInList(selected) ?? selected;
            if (!normalized.some(k => this.isSameKontakt(k, canonical))) {
                normalized.push(canonical);
            }
        }
        this.selectedKontakte = normalized;
    }

    private updateFilter(): void {
        this.deduplicateKontakte();
        let filtered = this.kontakte;

        // Filter nach Betriebsart basierend auf Config
        if (!this.config.showAll) {
            const allowedTypes: number[] = [];
            if (this.config.showLandwirte) {
                allowedTypes.push(...this.getLandwirtBetriebsarten());
            }
            if (this.config.showHaendler) {
                allowedTypes.push(9002);
            }
            if (this.config.showSchlachthoefe) {
                allowedTypes.push(9001);
            }

            if (allowedTypes.length > 0) {
                filtered = filtered.filter(
                    k =>
                        k.betriebsart_id == null ||
                        allowedTypes.includes(k.betriebsart_id) ||
                        this.isKontaktSelected(k),
                );
            }
        }

        // Filter nach Suchbegriff
        if (this.searchTerm) {
            const term = this.searchTerm.toLowerCase();
            filtered = filtered.filter(
                k =>
                    k.bezeichnung_final?.toLowerCase().includes(term) ||
                    k.zusatz_final?.toLowerCase().includes(term) ||
                    k.vvvo?.includes(term) ||
                    k.ort?.toLowerCase().includes(term),
            );
        }

        this.filteredKontakte = filtered;
    }

    private getLandwirtBetriebsarten(): number[] {
        // Alle Betriebsarten außer 9001 (Schlachthof) und 9002 (Händler)
        return this.kontakte
            .map(k => k.betriebsart_id)
            .filter(id => id !== 9001 && id !== 9002)
            .filter((id, index, arr) => arr.indexOf(id) === index); // Unique values
    }

    onSearchChange(event: any): void {
        this.searchTerm = event.target.value;
        this.updateFilter();
    }

    /**
     * Erweitert den Dialog-Bereich beim Klick auf das Eingabefeld
     */
    onSearchFocus(): void {
        this.isExpanded = true;
    }

    /**
     * Schließt den Dialog-Bereich beim Klick außerhalb
     */
    onSearchBlur(): void {
        // Verhindere Schließen wenn gerade auf Kontakt geklickt wurde
        if (this.preventBlur) {
            this.preventBlur = false;
            return;
        }

        // Schließe Dialog nur wenn keepOpenOnSelect nicht aktiviert ist
        if (!this.config.keepOpenOnSelect && !this.config.alwaysExpanded) {
            setTimeout(() => {
                this.isExpanded = false;
            }, 200);
        }
    }

    /**
     * Verhindert das Schließen des Dialogs beim Klick auf interaktive Elemente
     */
    onDialogClick(event: Event): void {
        event.stopPropagation();
        if (this.config.multiSelect) {
            this.isExpanded = true; // Bei Multi-Select: Dialog offen halten
        }
    }

    onKontaktClick(kontakt: Betriebskontakt): void {
        if (this.disabled) return;

        // Verhindere Schließen durch Blur wenn keepOpenOnSelect aktiviert ist
        if (this.config.keepOpenOnSelect) {
            this.isExpanded = true;
            this.preventBlur = true;
        }

        if (this.config.multiSelect) {
            this.toggleMultiSelect(kontakt);
        } else {
            this.selectSingleKontakt(kontakt);
        }
    }

    private selectSingleKontakt(kontakt: Betriebskontakt): void {
        this.selectedKontakte = [kontakt];

        if (this.config.multiSelect) {
            // Bei Multi-Select nur lokale Änderung emittieren
            this.selectionChanged.emit(this.selectedKontakte);
        } else {
            // Bei Single-Select direkt final auswählen
            this.kontakteSelected.emit([...this.selectedKontakte]);
            this.kontaktSelected.emit(kontakt);
        }
    }

    private toggleMultiSelect(kontakt: Betriebskontakt): void {
        const index = this.selectedKontakte.findIndex(k => this.isSameKontakt(k, kontakt));

        if (index >= 0) {
            this.selectedKontakte.splice(index, 1);
        } else {
            this.selectedKontakte.push(kontakt);
        }

        // Bei Multi-Select nur die lokale Änderung emittieren, nicht die finale Auswahl
        this.selectionChanged.emit(this.selectedKontakte);
    }

    /** Ensures a kontakt appears in the scrollable list (selected items are only visible if present in kontakte). */
    private ensureKontaktInList(kontakt: Betriebskontakt): void {
        if (!kontakt || this.findKontaktInList(kontakt)) {
            return;
        }
        this.kontakte.push(kontakt);
    }

    private mergeSelectedKontakteIntoList(): void {
        for (const kontakt of this.selectedKontakte) {
            this.ensureKontaktInList(kontakt);
        }
    }

    private addKontaktToSelection(kontakt: Betriebskontakt): void {
        this.deduplicateKontakte();
        const canonical = this.findKontaktInList(kontakt) ?? kontakt;
        this.ensureKontaktInList(canonical);
        this.deduplicateKontakte();

        if (this.config.multiSelect) {
            const alreadySelected = this.selectedKontakte.some(k => this.isSameKontakt(k, canonical));
            if (!alreadySelected) {
                this.selectedKontakte.push(canonical);
            }
            this.normalizeSelectedKontakteToList();
            this.selectionChanged.emit([...this.selectedKontakte]);
        } else {
            this.selectSingleKontakt(canonical);
        }

        this.isExpanded = true;
        this.updateFilter();
    }

    isKontaktSelected(kontakt: Betriebskontakt): boolean {
        return this.selectedKontakte.some(k => this.isSameKontakt(k, kontakt));
    }

    getBetriebsartBadgeColor(betriebsartId: number): string {
        switch (betriebsartId) {
            case 9002:
                return '#2196f3'; // Händler - Blau
            case 9001:
                return '#f44336'; // Schlachthof - Rot
            default:
                return '#4caf50'; // Landwirt - Grün
        }
    }

    getBetriebsartBadgeText(betriebsartId: number): string {
        switch (betriebsartId) {
            case 9002:
                return 'Händler';
            case 9001:
                return 'Schlachthof';
            default:
                return 'Landwirt';
        }
    }

    getVollAdresse(kontakt: Betriebskontakt): string {
        const parts = [];
        if (kontakt.strasse_final) parts.push(kontakt.strasse_final);
        if (kontakt.plz_final) parts.push(kontakt.plz_final);
        if (kontakt.ort_final) parts.push(kontakt.ort_final);
        return parts.join(' ');
    }

    getTelefonNummer(kontakt: Betriebskontakt): string | null {
        return kontakt.telefon_1 || kontakt.mobil_1 || kontakt.telefon_2 || kontakt.mobil_2 || null;
    }

    clearSelection(): void {
        this.selectedKontakte = [];
        this.kontakteSelected.emit(this.selectedKontakte);
        this.selectionChanged.emit(this.selectedKontakte);
    }

    auswaehlen(): void {
        // Emittiere die ausgewählten Kontakte
        this.kontakteSelected.emit([...this.selectedKontakte]);

        // Bei Single-Select auch den einzelnen Kontakt emittieren
        if (this.selectedKontakte.length === 1) {
            this.kontaktSelected.emit(this.selectedKontakte[0]);
        }

        // LÖSCHE NICHT die lokale Auswahl - der Parent entscheidet
        // this.selectedKontakte = [];
        // this.selectionChanged.emit(this.selectedKontakte);
    }

    getSelectedCount(): number {
        return this.selectedKontakte.length;
    }

    /**
     * Prüft, ob ein Kontakt bereits in den eigenen Kontakten ist
     */
    istKontaktBereitsVorhanden(kontakt: Betriebskontakt): boolean {
        return !!this.findKontaktInList(kontakt);
    }

    oeffneNeuenKontaktDialog(): void {
        const dialogRef = this.dialog.open(BetriebskontakteNeuerKontaktDialogComponent, {
            width: '70vw',
            maxWidth: '900px',
            minWidth: '320px',
            data: { vvvo: this.searchTerm },
            disableClose: true,
        });

        dialogRef.afterClosed().subscribe((result: NeuerKontaktDialogResult) => {
            if (result && result.success) {
                this.searchTerm = '';
                if (result.kontakt) {
                    const pending = result.kontakt;
                    this.ladeKontakteList(() => this.addKontaktToSelection(pending));
                    return;
                }

                this.ladeKontakteList();
            }
        });
    }
}
